import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { LANGUAGES } from "../lib/i18n";
import { Wrench, Plus, MessageSquare, Bell, User as UserIcon, LogOut, Menu, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Header() {
  const { user, login, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const prevMsgs = useRef(null);
  const prevNotifs = useRef(null);

  useEffect(() => {
    if (!user) { setUnread(0); setUnreadMsgs(0); prevMsgs.current = null; prevNotifs.current = null; return; }
    let cancelled = false;
    const load = async () => {
      try {
        const [n, m] = await Promise.all([
          api.get("/notifications"),
          api.get("/messages/unread-count"),
        ]);
        if (cancelled) return;
        const newUnreadNotifs = n.data.filter((x) => !x.is_read).length;
        const newUnreadMsgs = m.data.count || 0;

        // Toast on new arrival (only when on a different page than messages)
        if (prevMsgs.current !== null && newUnreadMsgs > prevMsgs.current && !location.pathname.startsWith("/messages")) {
          toast.info(t("alert_match_msg") || "Nauja žinutė!", {
            description: t("click_to_open") || "Spausk, kad atidarytum",
            action: { label: t("nav_messages"), onClick: () => navigate("/messages") },
          });
        }
        if (prevNotifs.current !== null && newUnreadNotifs > prevNotifs.current && location.pathname !== "/alerts") {
          toast.info(t("alert_match") || "Naujas pranešimas!", {
            action: { label: t("nav_alerts"), onClick: () => navigate("/alerts") },
          });
        }
        prevMsgs.current = newUnreadMsgs;
        prevNotifs.current = newUnreadNotifs;
        setUnread(newUnreadNotifs);
        setUnreadMsgs(newUnreadMsgs);
      } catch (_e) {/* noop */}
    };
    load();
    const id = setInterval(load, 6000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, location.pathname, navigate, t]);

  const NavLink = ({ to, children, testId, mobile = false }) => (
    <Link
      to={to}
      onClick={() => setOpen(false)}
      data-testid={testId}
      className={`${mobile ? "block w-full" : ""} px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
        location.pathname === to
          ? "bg-[#1A1A18] text-white"
          : "text-[#4A4A45] hover:bg-[#F0EFEB] hover:text-[#1A1A18]"
      }`}
    >
      {children}
    </Link>
  );

  return (
    <header className="header-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 bg-[#C85A32] rounded-md flex items-center justify-center group-hover:rotate-6 transition-transform">
              <Wrench className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight text-[#1A1A18]">
              StopChlamas
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" testId="nav-home">{t("nav_home")}</NavLink>
            <NavLink to="/browse" testId="nav-browse">{t("nav_browse")}</NavLink>
            {user && (
              <>
                <NavLink to="/post" testId="nav-post">
                  <span className="inline-flex items-center gap-1.5"><Plus className="w-4 h-4"/>{t("nav_post")}</span>
                </NavLink>
                <NavLink to="/messages" testId="nav-messages">
                  <span className="inline-flex items-center gap-1.5 relative">
                    <MessageSquare className="w-4 h-4"/>{t("nav_messages")}
                    {unreadMsgs > 0 && (
                      <span data-testid="unread-msgs-badge" className="absolute -top-2 -right-3 bg-[#C85A32] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center pulse-dot">
                        {unreadMsgs}
                      </span>
                    )}
                  </span>
                </NavLink>
                <NavLink to="/alerts" testId="nav-alerts">
                  <span className="inline-flex items-center gap-1.5 relative">
                    <Bell className="w-4 h-4"/>{t("nav_alerts")}
                    {unread > 0 && (
                      <span data-testid="unread-badge" className="absolute -top-2 -right-3 bg-[#C85A32] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                        {unread}
                      </span>
                    )}
                  </span>
                </NavLink>
              </>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {/* Language selector */}
            <div className="hidden sm:flex items-center bg-[#F0EFEB] rounded-md p-0.5">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded transition-colors ${
                    lang === l.code ? "bg-[#1A1A18] text-white" : "text-[#4A4A45] hover:text-[#1A1A18]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Link
                  to="/profile"
                  data-testid="profile-link"
                  className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-[#F0EFEB]"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#1A1A18] text-white flex items-center justify-center text-xs font-bold">
                      {user.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm font-medium text-[#1A1A18] hidden lg:inline">{user.name}</span>
                </Link>
                <button
                  data-testid="logout-button"
                  onClick={() => { logout(); navigate("/"); }}
                  className="p-2 rounded-md hover:bg-[#F0EFEB] text-[#4A4A45]"
                  title={t("cta_logout")}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                data-testid="login-button"
                onClick={login}
                className="hidden md:inline-flex bg-[#1A1A18] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#C85A32] transition-colors"
              >
                {t("cta_login")}
              </button>
            )}

            <button
              className="md:hidden p-2 rounded-md hover:bg-[#F0EFEB] relative"
              onClick={() => setOpen(!open)}
              data-testid="menu-toggle"
            >
              {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
              {!open && (unread + unreadMsgs) > 0 && (
                <span data-testid="mobile-unread-dot" className="absolute top-1 right-1 w-2.5 h-2.5 bg-[#C85A32] rounded-full pulse-dot"/>
              )}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden pb-4 border-t border-[#D1CFC7] pt-3 flex flex-col gap-1" data-testid="mobile-menu">
            <NavLink to="/" testId="mnav-home" mobile>{t("nav_home")}</NavLink>
            <NavLink to="/browse" testId="mnav-browse" mobile>{t("nav_browse")}</NavLink>
            {user ? (
              <>
                <NavLink to="/post" testId="mnav-post" mobile>+ {t("nav_post")}</NavLink>
                <NavLink to="/messages" testId="mnav-messages" mobile>{t("nav_messages")}{unreadMsgs > 0 && ` (${unreadMsgs})`}</NavLink>
                <NavLink to="/alerts" testId="mnav-alerts" mobile>{t("nav_alerts")}{unread > 0 && ` (${unread})`}</NavLink>
                <NavLink to="/profile" testId="mnav-profile" mobile>{t("nav_profile")}</NavLink>
                <button
                  data-testid="m-logout-button"
                  onClick={() => { logout(); setOpen(false); navigate("/"); }}
                  className="block w-full text-left px-4 py-2 rounded-md text-sm font-semibold text-[#C85A32] hover:bg-[#F0EFEB]"
                >
                  {t("cta_logout")}
                </button>
              </>
            ) : (
              <button
                data-testid="m-login-button"
                onClick={() => { login(); setOpen(false); }}
                className="block w-full bg-[#1A1A18] text-white text-sm font-semibold px-4 py-2 rounded-md text-left"
              >
                {t("cta_login")}
              </button>
            )}
            <div className="flex gap-2 pt-2">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`m-lang-${l.code}`}
                  className={`flex-1 px-2 py-1.5 text-xs font-bold rounded ${
                    lang === l.code ? "bg-[#1A1A18] text-white" : "bg-[#F0EFEB] text-[#4A4A45]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
